/**
 * Report themes: the four built-in looks AgencyAnalytics offers (Classic,
 * Elegant, Playful, Bold) and how a theme becomes CSS variables on the page.
 * The API checks every theme (fonts from THEME_FONTS, #rrggbb colours, a
 * small PNG/JPEG/WebP background), so these values are safe to put in style.
 */

import type { CSSProperties } from 'react';

export const THEME_FONTS = [
  'Inter',
  'Georgia',
  'Merriweather',
  'Poppins',
  'Roboto',
  'Arial',
  'Helvetica',
] as const;
export type ThemeFont = (typeof THEME_FONTS)[number];

export interface ReportTheme {
  preset?: 'classic' | 'elegant' | 'playful' | 'bold' | 'custom';
  name?: string;
  title_font?: ThemeFont;
  body_font?: ThemeFont;
  background_color?: string;
  background_image?: string;
  background_fit?: 'fit' | 'cover';
  card_color?: string;
  text_color?: string;
  accent_color?: string;
  series?: string[];
}

export const PRESET_THEMES: ReportTheme[] = [
  {
    preset: 'classic',
    name: 'Classic',
    title_font: 'Inter',
    body_font: 'Inter',
    background_color: '#f3f5f8',
    card_color: '#ffffff',
    text_color: '#1f2a37',
    accent_color: '#0072ee',
    series: [
      '#0072ee',
      '#00a3a3',
      '#f59e0b',
      '#e5484d',
      '#7c5cdb',
      '#16a34a',
      '#64748b',
      '#db2777',
    ],
  },
  {
    preset: 'elegant',
    name: 'Elegant',
    title_font: 'Merriweather',
    body_font: 'Georgia',
    background_color: '#f6f1ea',
    card_color: '#fffdf9',
    text_color: '#2b2620',
    accent_color: '#8c6d46',
    series: [
      '#8c6d46',
      '#3f5e5a',
      '#b4874f',
      '#6b4f6e',
      '#7a8b5a',
      '#a1594a',
      '#4c5c7a',
      '#c2a477',
    ],
  },
  {
    preset: 'playful',
    name: 'Playful',
    title_font: 'Poppins',
    body_font: 'Poppins',
    background_color: '#fff4e8',
    card_color: '#ffffff',
    text_color: '#2d1b4e',
    accent_color: '#e8437a',
    series: [
      '#e8437a',
      '#ff9f1c',
      '#2ec4b6',
      '#6a4cff',
      '#00a6fb',
      '#9bc53d',
      '#f15bb5',
      '#fee440',
    ],
  },
  {
    preset: 'bold',
    name: 'Bold',
    title_font: 'Poppins',
    body_font: 'Roboto',
    background_color: '#14213d',
    card_color: '#ffffff',
    text_color: '#111827',
    accent_color: '#e85d04',
    series: [
      '#e85d04',
      '#14213d',
      '#2a9d8f',
      '#e9c46a',
      '#6d597a',
      '#457b9d',
      '#9d0208',
      '#8d99ae',
    ],
  },
];

const FONT_STACK: Record<ThemeFont, string> = {
  Inter: "'Inter', system-ui, sans-serif",
  Georgia: "Georgia, 'Times New Roman', serif",
  Merriweather: "'Merriweather', Georgia, serif",
  Poppins: "'Poppins', system-ui, sans-serif",
  Roboto: "'Roboto', system-ui, sans-serif",
  Arial: 'Arial, Helvetica, sans-serif',
  Helvetica: 'Helvetica, Arial, sans-serif',
};

/** Web fonts that are not already on the page (Inter is) or built into every system. */
export const WEB_FONTS: Partial<Record<ThemeFont, string>> = {
  Merriweather: 'Merriweather:wght@400;700',
  Poppins: 'Poppins:wght@400;500;600;700',
  Roboto: 'Roboto:wght@400;500;700',
};

const DARK_GROUND_LUMINANCE = 0.4;
const LIGHT_INK = '#ffffff';
const RGB_MAX = 255;
const LINEAR_THRESHOLD = 0.04045;

/** Relative luminance (WCAG) of #rrggbb. */
export function luminance(hex: string): number {
  const channels = [1, 3, 5].map((at) => Number.parseInt(hex.slice(at, at + 2), 16) / RGB_MAX);
  const [r, g, b] = channels.map((value) =>
    value <= LINEAR_THRESHOLD ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4,
  ) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

const mix = (colour: string, percent: number, into: string) =>
  `color-mix(in oklab, ${colour} ${percent}%, ${into})`;

export function isThemed(theme: ReportTheme | Record<string, unknown> | undefined): boolean {
  return Boolean(theme && Object.keys(theme).length > 0);
}

/** The CSS variables a theme sets; empty when the theme is the default look. */
export function themeVariables(theme: ReportTheme): CSSProperties {
  if (!isThemed(theme)) return {};
  const vars: Record<string, string> = {};
  const card = theme.card_color ?? '#ffffff';
  const text = theme.text_color ?? '#1f2a37';
  const ground = theme.background_color ?? card;
  // Text drawn straight on the ground (section titles, covers) must read on it.
  const groundInk = luminance(ground) < DARK_GROUND_LUMINANCE ? LIGHT_INK : text;
  vars['--rt-ground'] = ground;
  vars['--rt-card'] = card;
  vars['--ink'] = groundInk;
  vars['--ink-soft'] = mix(groundInk, 78, ground);
  vars['--ink-faint'] = mix(groundInk, 64, ground);
  vars['--rt-c-ink'] = text;
  vars['--rt-c-soft'] = mix(text, 78, card);
  vars['--rt-c-faint'] = mix(text, 64, card);
  vars['--rt-c-rule'] = mix(text, 14, card);
  if (theme.accent_color) {
    vars['--accent'] = theme.accent_color;
    vars['--accent-strong'] = theme.accent_color;
    vars['--accent-ink'] = theme.accent_color;
    vars['--accent-soft'] = mix(theme.accent_color, 13, 'transparent');
  }
  if (theme.background_image) {
    vars['--rt-image'] = `url("${theme.background_image}")`;
    vars['--rt-fit'] = theme.background_fit === 'fit' ? 'contain' : 'cover';
  }
  if (theme.title_font) vars['--rt-title-font'] = FONT_STACK[theme.title_font];
  if (theme.body_font) vars['--rt-body-font'] = FONT_STACK[theme.body_font];
  (theme.series ?? []).forEach((colour, index) => {
    vars[`--series-${index}`] = colour;
  });
  return vars as CSSProperties;
}

/** Same theme, ignoring key order: is this preset the one applied? */
export function sameTheme(left: ReportTheme, right: ReportTheme): boolean {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every(
    (key) =>
      JSON.stringify(left[key as keyof ReportTheme]) ===
      JSON.stringify(right[key as keyof ReportTheme]),
  );
}
